import * as React from "react";
import { Button, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { AccessibleAlert } from "../../../../components/AccessibleAlert/AccessibleAlert";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import { SelectManyAutocompleteField } from "components/formikFields/SelectManyAutocompleteField/SelectManyAutocompleteField";
import { CustomField } from "components/formikFields/CustomField/CustomField";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import {
  ClientConfigsService,
  CustomFieldDefinition,
  Language,
  LltsLanguage,
  UserAccountsService,
  UtilsService,
  WorkflowOption
} from "gen/clients/llts";
import { SelectOption } from "@mui/base";
import Stack from "@mui/material/Stack";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { FormFieldObserver } from "components/formikFields/FormFieldObserver/FormFieldObserver";
import { useUserSession } from "../../../../hooks/useUserSession";
import { ButtonWithOptions } from "../../../../components/ButtonWithOptions/ButtonWithOptions";
import { SELECT_OPTION_COMPARATOR } from "../../../../utils/stringUtils";
import { useFormikContext } from "formik";
import { removeDuplicatesByProperty } from "../../../../utils/arrayUtils";
import { CustomFieldUtils } from "components/formikFields/CustomField/CustomFieldUtils";
import { useDialogState } from "../../../../hooks/useDialogState/useDialogState";
import { ContentInputMethod, FilesSectionFieldNames } from "../FilesSection/FilesSection";
import { DetectLanguageDialog } from "components/DetectLanguageDialog/DetectLanguageDialog";

interface Props {
  clientId?: string;
  projectCustomFieldDefinitions?: CustomFieldDefinition[];
  workflowOptions?: WorkflowOption[];
  isProjectNameVisible?: boolean;
  hideLanguages?: boolean;
  hideSourceLanguage?: boolean;
  disableSourceLanguage?: boolean;
  disableShowAllLanguagesForSourceLanguage?: boolean;
  disableSourceLanguageDetection?: boolean;
  hideTargetLanguages?: boolean;
  disableTargetLanguages?: boolean;
  disableShowAllLanguagesForTargetLanguages?: boolean;
}

const LANGUAGE_COMPARATOR = (l1: Language, l2: Language) => {
  if (l1.preferred && !l2.preferred) {
    return -1;
  }
  if (!l1.preferred && l2.preferred) {
    return 1;
  }
  return l1.name.localeCompare(l2.name);
};

const WORKFLOW_COMPARATOR = (w1: WorkflowOption, w2: WorkflowOption) => w1.label.localeCompare(w2.label);

export enum ProjectInfoFieldName {
  projectName = "projectName",
  sourceLanguage = "sourceLanguage",
  targetLanguages = "targetLanguages",
  notes = "notes",
  xtrfServiceId = "xtrfServiceId"
}

export interface ProjectInfoFormValues {
  [ProjectInfoFieldName.sourceLanguage]: SelectOption<string> | null;
  [ProjectInfoFieldName.targetLanguages]: SelectOption<string>[];
  [ProjectInfoFieldName.notes]: string;
  [ProjectInfoFieldName.projectName]: string | undefined;
  [ProjectInfoFieldName.xtrfServiceId]: string;
}

const ProjectInfoSection: React.FC<Props> = ({
  clientId,
  projectCustomFieldDefinitions,
  workflowOptions,
  isProjectNameVisible,
  hideLanguages,
  hideSourceLanguage,
  disableSourceLanguage,
  disableShowAllLanguagesForSourceLanguage,
  disableSourceLanguageDetection,
  hideTargetLanguages,
  disableTargetLanguages,
  disableShowAllLanguagesForTargetLanguages
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<ProjectInfoFormValues>();
  const { username } = useUserSession();
  const [sourceLanguagesDisplayAll, setSourceLanguagesDisplayAll] = React.useState(false);
  const [targetLanguagesDisplayAll, setTargetLanguagesDisplayAll] = React.useState(false);

  const [selectedSourceLanguage, setSelectedSourceLanguage] = React.useState<string | null>(null);
  const [selectedTargetLanguages, setSelectedTargetLanguages] = React.useState<string[]>([]);
  const [isDetectDialogOpen, openDetectDialog, closeDetectDialog] = useDialogState();
  const [detectedLanguageNotAvailable, setDetectedLanguageNotAvailable] = React.useState<string>();

  const onOpenDetectDialog = React.useCallback(() => {
    setDetectedLanguageNotAvailable(undefined);
    openDetectDialog();
  }, [openDetectDialog]);

  const inputMethod = (values as unknown as Record<string, unknown>)[FilesSectionFieldNames.inputMethod] as
    | ContentInputMethod
    | undefined;
  const files =
    ((values as unknown as Record<string, unknown>)[FilesSectionFieldNames.files] as File[] | undefined) || [];
  const isDetectLanguageButtonVisible = inputMethod === ContentInputMethod.FILES && !disableSourceLanguageDetection;
  const isDetectLanguageButtonEnabled = files.length > 0;

  const {
    data: languages,
    isLoading: isLanguagesLoading,
    error: languagesError
  } = useQuery("listLanguages", {
    enabled: !hideLanguages,
    queryFn: UtilsService.listLanguages
  });
  const { data: availableLanguages, isLoading: isAvailableLanguagesLoading } = useQuery(
    ["getAvailableLanguages", clientId],
    {
      enabled: !!clientId && !hideLanguages,
      queryFn: () => ClientConfigsService.getAvailableLanguages({ clientId: clientId || "" })
    }
  );
  const { data: userLanguageGroups } = useQuery(["listLanguageGroups", username], {
    enabled: !!username && !hideLanguages,
    queryFn: UserAccountsService.listLanguageGroups
  });
  // Unauthenticated users cannot access the user account endpoint, so the client's organization groups are fetched instead.
  const { data: clientLanguageGroups } = useQuery(["getClientLanguageGroups", clientId], {
    enabled: !username && !!clientId && !hideLanguages,
    queryFn: () => ClientConfigsService.getClientLanguageGroups({ clientId: clientId || "" })
  });

  const languageGroups = username ? userLanguageGroups : clientLanguageGroups;

  const languageGroupOptions = React.useMemo(() => {
    return languageGroups?.map(lg => ({ label: lg.name, value: `${lg.name}` })).sort(SELECT_OPTION_COMPARATOR) || [];
  }, [languageGroups]);

  const selectableWorkflowOptions = React.useMemo(
    () =>
      workflowOptions
        ? workflowOptions.sort(WORKFLOW_COMPARATOR).map(w => ({ label: w.label, value: `${w.xtrfServiceId}` }))
        : [],
    [workflowOptions]
  );

  const sourceLanguageOptions = React.useMemo(() => {
    const langs =
      sourceLanguagesDisplayAll || !availableLanguages?.length
        ? languages || []
        : languages?.filter(l => availableLanguages.includes(+l.id)) || [];
    return langs.sort(LANGUAGE_COMPARATOR).map(l => ({ label: l.name, value: `${l.id}` })) || [];
  }, [languages, availableLanguages, sourceLanguagesDisplayAll]);
  const targetLanguageOptions = React.useMemo(() => {
    const langs =
      targetLanguagesDisplayAll || !availableLanguages?.length
        ? languages || []
        : languages?.filter(l => availableLanguages.includes(+l.id)) || [];
    return langs.sort(LANGUAGE_COMPARATOR).map(l => ({ label: l.name, value: `${l.id}` })) || [];
  }, [languages, availableLanguages, targetLanguagesDisplayAll]);

  const onLanguageDetected = React.useCallback(
    (lang: LltsLanguage) => {
      const matchingOption = lang.xtrfId
        ? sourceLanguageOptions.find(opt => opt.value === `${lang.xtrfId}`)
        : undefined;
      if (matchingOption) {
        setFieldValue(ProjectInfoFieldName.sourceLanguage, matchingOption);
      } else {
        setDetectedLanguageNotAvailable(lang.name);
      }
    },
    [sourceLanguageOptions, setFieldValue]
  );

  const isSameLanguageSelected = React.useMemo(() => {
    return selectedSourceLanguage !== null && selectedTargetLanguages.includes(selectedSourceLanguage);
  }, [selectedTargetLanguages, selectedSourceLanguage]);

  const currentTargetLanguages = values[ProjectInfoFieldName.targetLanguages];
  const onLanguageGroupSelect = React.useCallback(
    (languageGroupName: string) => {
      const languageGroup = languageGroups?.find(lg => lg.name === languageGroupName);
      if (!languageGroup) {
        window.console.log(`Language group ${languageGroupName} not found`);
        return;
      }
      const groupLanguageOptions: SelectOption<string>[] = languageGroup.languages
        .filter(l => l.xtrfId)
        .filter(l => languages?.find(lang => lang.id === l.xtrfId))
        .map(l => ({ label: languages?.find(lang => lang.id === l.xtrfId)?.name || l.name, value: `${l.xtrfId}` }));

      const newTargetLanguages = removeDuplicatesByProperty(
        [...currentTargetLanguages, ...groupLanguageOptions],
        "value"
      ).sort(SELECT_OPTION_COMPARATOR);
      setFieldValue(ProjectInfoFieldName.targetLanguages, newTargetLanguages);
    },
    [languageGroups, currentTargetLanguages, setFieldValue, languages]
  );

  return (
    <Grid container spacing={4}>
      {isProjectNameVisible && (
        <Grid item xs={12}>
          <TextInputField
            name={ProjectInfoFieldName.projectName}
            label={t("createProject.createProjectForm.projectName.label")}
            helperText={t("createProject.createProjectForm.projectName.helperText")}
            required={true}
            preventCharacters={[";"]}
            preventCharactersMessage={t("common.validation.semicolonNotAllowed")}
            maxLength={140}
            maxLengthMessage={t("common.validation.projectNameMaxLength")}
          />
        </Grid>
      )}
      {!hideLanguages && (
        <>
          {!hideSourceLanguage && (
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <FormFieldObserver
                  fieldName={ProjectInfoFieldName.sourceLanguage}
                  onChange={(option: SelectOption<string> | null) => {
                    setSelectedSourceLanguage(option ? option.value : null);
                  }}
                />
                <SelectOneAutocompleteField
                  name={ProjectInfoFieldName.sourceLanguage}
                  label={t("createProject.createProjectForm.sourceLanguage.label")}
                  helperText={t("createProject.createProjectForm.sourceLanguage.helperText")}
                  options={sourceLanguageOptions}
                  isLoading={isLanguagesLoading || isAvailableLanguagesLoading}
                  isError={!!languagesError}
                  required={true}
                  disabled={disableSourceLanguage}
                />
                {isDetectLanguageButtonVisible && (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={onOpenDetectDialog}
                      disabled={!isDetectLanguageButtonEnabled || disableSourceLanguage}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {t("createProject.createProjectForm.detectLanguage.button")}
                    </Button>
                  </div>
                )}
                {(availableLanguages?.length || 0) > 0 && (
                  <FormControlLabel
                    label={
                      <span style={{ whiteSpace: "nowrap" }}>
                        {t("createProject.createProjectForm.sourceLanguage.moreOptions")}
                      </span>
                    }
                    control={
                      <Checkbox
                        checked={sourceLanguagesDisplayAll}
                        onChange={() => setSourceLanguagesDisplayAll(prevState => !prevState)}
                        disabled={disableSourceLanguage || disableShowAllLanguagesForSourceLanguage}
                      />
                    }
                  />
                )}
              </Stack>
              {detectedLanguageNotAvailable && (
                <AccessibleAlert severity="warning" sx={{ mt: 1 }}>
                  {t("createProject.createProjectForm.detectLanguage.languageNotAvailable", {
                    languageName: detectedLanguageNotAvailable
                  })}
                </AccessibleAlert>
              )}
              {isDetectDialogOpen && (
                <DetectLanguageDialog
                  files={files}
                  onLanguageDetected={onLanguageDetected}
                  onClose={closeDetectDialog}
                />
              )}
            </Grid>
          )}
          {!hideTargetLanguages && (
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <FormFieldObserver
                  fieldName={ProjectInfoFieldName.targetLanguages}
                  onChange={(options: SelectOption<string>[]) =>
                    setSelectedTargetLanguages(options.map(option => option.value))
                  }
                />
                <SelectManyAutocompleteField
                  name={ProjectInfoFieldName.targetLanguages}
                  label={t("createProject.createProjectForm.targetLanguage.label")}
                  helperText={t("createProject.createProjectForm.targetLanguage.helperText")}
                  options={targetLanguageOptions}
                  isLoading={isLanguagesLoading || isAvailableLanguagesLoading}
                  isError={!!languagesError}
                  required={true}
                  disabled={disableTargetLanguages}
                />
                {(availableLanguages?.length || 0) > 0 && (
                  <FormControlLabel
                    label={
                      <span style={{ whiteSpace: "nowrap" }}>
                        {t("createProject.createProjectForm.sourceLanguage.moreOptions")}
                      </span>
                    }
                    control={
                      <Checkbox
                        checked={targetLanguagesDisplayAll}
                        onChange={() => setTargetLanguagesDisplayAll(prevState => !prevState)}
                        disabled={disableTargetLanguages || disableShowAllLanguagesForTargetLanguages}
                      />
                    }
                  />
                )}
                {languageGroupOptions.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ButtonWithOptions
                      options={languageGroupOptions}
                      label={<span style={{ whiteSpace: "nowrap" }}>Language Groups</span>}
                      onSelect={onLanguageGroupSelect}
                      variant="outlined"
                      size="small"
                      disabled={disableTargetLanguages}
                    />
                  </div>
                )}
              </Stack>
            </Grid>
          )}

          {isSameLanguageSelected && (
            <Grid item xs={12}>
              <AccessibleAlert severity="warning">
                {t("createProject.createProjectForm.sameLanguageWarning")}
              </AccessibleAlert>
            </Grid>
          )}

          {selectableWorkflowOptions && selectableWorkflowOptions.length > 0 && (
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <SelectOneField
                  name={ProjectInfoFieldName.xtrfServiceId}
                  label={t("createProject.createProjectForm.selectableWorkflow.label")}
                  helperText={t("createProject.createProjectForm.selectableWorkflow.helperText")}
                  options={selectableWorkflowOptions}
                  required={true}
                />
              </Stack>
            </Grid>
          )}
        </>
      )}
      {/* Project custom fields */}
      {projectCustomFieldDefinitions &&
        projectCustomFieldDefinitions.map(customFieldDefinition => (
          <React.Fragment key={customFieldDefinition.name}>
            {CustomFieldUtils.isCustomFieldVisible(customFieldDefinition, values) && (
              <Grid item xs={12}>
                <CustomField customFieldDefinition={customFieldDefinition} />
              </Grid>
            )}
          </React.Fragment>
        ))}
      {/* Notes */}
      <Grid item xs={12}>
        <TextInputField
          name={ProjectInfoFieldName.notes}
          label={t("createProject.createProjectForm.notes")}
          multiline={true}
          rows={5}
          variant="outlined"
        />
      </Grid>
    </Grid>
  );
};

export { ProjectInfoSection };
